import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, PageChangeEvent } from '@progress/kendo-angular-grid';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AvailableProduct, BillingService } from '../../../../services/billing.service';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';

@Component({
  selector: 'app-available-products',
  standalone: true,
  imports: [CommonModule, GridModule, IndicatorsModule],
  templateUrl: './available-products.component.html',
  styleUrl: './available-products.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailableProductsComponent extends BaseComponent {
  private billingService = inject(BillingService);

  public organizationId = input.required<number>();

  public products = signal<AvailableProduct[]>([]);
  public isLoading = signal<boolean>(true);

  public skip = signal<number>(0);
  public pageSize = signal<number>(10);
  public pagedProducts = computed(() => {
    const start = this.skip();
    return {
      data: this.products().slice(start, start + this.pageSize()),
      total: this.products().length,
    };
  });

  constructor() {
    super();
    effect(
      () => {
        const id = this.organizationId();
        if (!id) return;
        this.load(id);
      },
      { allowSignalWrites: true }
    );
  }

  public onPageChange(event: PageChangeEvent): void {
    this.skip.set(event.skip);
    this.pageSize.set(event.take);
  }

  private load(id: number): void {
    this.isLoading.set(true);
    this.billingService
      .getAvailableProducts(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.products.set(res.products);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to load available products:', err);
          this.isLoading.set(false);
        },
      });
  }
}
